import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Store, Loader2, Package, ShoppingCart, Sparkles, Coins, HandCoins,
} from 'lucide-react';
import {
  getItems, buyItem, getVendors, buyAlz, getMarketStats,
  type RmtItemMarket, type RmtVendor,
} from '@/shared/services/rmtService';
import { getCharacters, type Character } from '@/shared/services/characterService';
import { API_URL } from '@/shared/services/authServices';
import { useToast } from '@/shared/contexts/ToastContext';

function trim(x: number): string {
  return Number(x.toFixed(2)).toString();
}
function fmtAlzShort(n: number): string {
  if (!n) return '0';
  if (n >= 1e9) return trim(n / 1e9) + 'B';
  if (n >= 1e6) return trim(n / 1e6) + 'kk';
  return n.toLocaleString('pt-BR');
}
const fmtBrl = (n: number) =>
  'R$ ' + (n ?? 0).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

// A API (pydantic to_camel) serializa CharacterIdx como 'characteridx'.
function charId(c: Character): number {
  return Number((c as any).characteridx ?? (c as any).characterIdx ?? 0);
}

function ItemIcon({ itemId }: { itemId: number | null }) {
  const [ok, setOk] = useState(true);
  if (!itemId || !ok) {
    return (
      <div className="w-14 h-14 rounded-lg bg-[#0d141b] border border-white/[0.06]
                      flex items-center justify-center shrink-0">
        <Package size={20} className="text-white/25" />
      </div>
    );
  }
  return (
    <div className="w-14 h-14 rounded-lg bg-[#0d141b] border border-white/[0.06] overflow-hidden shrink-0">
      <img src={`${API_URL}/admin/item-icon/${itemId}.png?v=nobg2`} onError={() => setOk(false)}
           alt="" className="w-full h-full object-contain" loading="lazy" />
    </div>
  );
}

function itemBadges(it: RmtItemMarket): { text: string; cls: string }[] {
  const b: { text: string; cls: string }[] = [];
  if (it.upgrade > 0) b.push({ text: `+${it.upgrade}`, cls: 'bg-amber-500/15 text-amber-400' });
  if (it.divine > 0) b.push({ text: `Divine +${it.divine}`, cls: 'bg-purple-500/15 text-purple-300' });
  if (it.extreme > 0) b.push({ text: `Extreme +${it.extreme}`, cls: 'bg-cyan-500/15 text-cyan-300' });
  if (it.slotCount > 0) b.push({ text: `${it.slotCount} slot${it.slotCount > 1 ? 's' : ''}`, cls: 'bg-white/10 text-white/70' });
  if (it.sealed) b.push({ text: 'Selado', cls: 'bg-white/10 text-white/60' });
  if (it.bound) b.push({ text: 'Vinculado', cls: 'bg-red-500/10 text-red-300' });
  return b;
}


export default function MarketplacePage() {
  const { notify } = useToast();
  const [tab, setTab] = useState<'itens' | 'alz'>('itens');
  const [loading, setLoading] = useState(true);

  const [chars, setChars] = useState<Character[]>([]);
  const [charIdx, setCharIdx] = useState<number | null>(null);

  const [items, setItems] = useState<RmtItemMarket[]>([]);
  const [buyingItem, setBuyingItem] = useState<number | null>(null);

  const [vendors, setVendors] = useState<RmtVendor[]>([]);
  const [vendorUserNum, setVendorUserNum] = useState<number | null>(null);
  const [units, setUnits] = useState(1);
  const [unitAlz, setUnitAlz] = useState(100_000_000);
  const [buyingAlz, setBuyingAlz] = useState(false);

  const fetchItems = useCallback(async () => {
    try { setItems(await getItems()); } catch { /* silencioso */ }
  }, []);
  const fetchVendors = useCallback(async () => {
    try { setVendors(await getVendors()); } catch { /* silencioso */ }
  }, []);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const [cs, its, vs, st] = await Promise.all([
          getCharacters(), getItems(), getVendors(), getMarketStats('7d'),
        ]);
        setChars(cs);
        setItems(its);
        setVendors(vs);
        setUnitAlz(st.unitAlz || 100_000_000);
      } catch { /* silencioso */ }
      finally { setLoading(false); }
    })();
  }, []);

  useEffect(() => {
    if (charIdx === null && chars.length > 0) setCharIdx(charId(chars[0]));
  }, [chars, charIdx]);

  useEffect(() => {
    if (vendors.length === 0) { setVendorUserNum(null); return; }
    if (vendorUserNum === null || !vendors.some(v => v.userNum === vendorUserNum)) {
      setVendorUserNum(vendors[0].userNum);
    }
  }, [vendors, vendorUserNum]);

  const selectedVendor = vendors.find(v => v.userNum === vendorUserNum) ?? null;
  const maxUnits = selectedVendor
    ? Math.max(1, Math.min(1000, Math.floor(selectedVendor.availableAlz / unitAlz)))
    : 1000;
  useEffect(() => { setUnits(u => Math.min(u, maxUnits)); }, [maxUnits]);

  const totalAlz = useMemo(() => units * unitAlz, [units, unitAlz]);
  const totalBrl = selectedVendor ? units * selectedVendor.alzPriceBrl : 0;

  const handleBuyItem = async (it: RmtItemMarket) => {
    if (!charIdx) { notify('error', 'Selecione um personagem'); return; }
    setBuyingItem(it.listingId);
    try {
      const res = await buyItem(it.listingId, charIdx);
      notify('success', res.message);
      await fetchItems();
    } catch (err: any) {
      notify('error', err?.detail || err?.message || 'Erro na compra');
      await fetchItems();
    } finally {
      setBuyingItem(null);
    }
  };

  const handleBuyAlz = async () => {
    if (!vendorUserNum) { notify('error', 'Selecione um vendedor'); return; }
    if (!charIdx) { notify('error', 'Selecione um personagem'); return; }
    setBuyingAlz(true);
    try {
      const res = await buyAlz(units, charIdx, vendorUserNum);
      notify('success', res.message);
      await fetchVendors();
    } catch (err: any) {
      notify('error', err?.detail || err?.message || 'Erro na compra');
      await fetchVendors();
    } finally {
      setBuyingAlz(false);
    }
  };

  return (
    <div>
      <div className="flex items-center gap-3 mb-6">
        <h1 className="text-2xl md:text-3xl font-bold text-white flex items-center gap-3">
          <Store className="text-[#8fb9de]" size={26} />
          Marketplace
        </h1>
        <span className="ml-auto text-[10px] font-black uppercase tracking-wider px-2 py-1
                         rounded bg-amber-500/15 text-amber-400 border border-amber-400/30
                         flex items-center gap-1">
          <Sparkles size={11} /> Sandbox
        </span>
      </div>

      {/* Personagem de entrega */}
      <div className="surface-card rounded-xl p-4 mb-5">
        <label className="text-xs font-bold text-white/70 block mb-1.5">
          Entregar a compra no personagem
        </label>
        {chars.length === 0 ? (
          <p className="text-[11px] text-amber-400">
            Você precisa de um personagem para receber a compra.
          </p>
        ) : (
          <select
            value={charIdx ?? ''}
            onChange={(e) => setCharIdx(Number(e.target.value))}
            className="w-full sm:w-auto bg-[#0d141b] border border-white/[0.06] rounded-lg
                       px-4 py-2.5 text-sm text-white"
          >
            {chars.map(c => (
              <option key={charId(c)} value={charId(c)}>
                {c.name} — Nível {c.lev}
              </option>
            ))}
          </select>
        )}
      </div>

      {/* Tabs */}
      <div className="surface-card rounded-xl overflow-hidden">
        <div className="grid grid-cols-2 border-b border-white/[0.06]">
          <button
            onClick={() => setTab('itens')}
            className={`py-3 text-sm font-bold flex items-center justify-center gap-2 transition cursor-pointer
                        ${tab === 'itens' ? 'bg-[#8fb9de]/10 text-[#8fb9de]'
                                          : 'text-white/55 hover:bg-white/[0.03]'}`}
          >
            <Package size={15} /> ITENS
          </button>
          <button
            onClick={() => setTab('alz')}
            className={`py-3 text-sm font-bold flex items-center justify-center gap-2 transition cursor-pointer
                        ${tab === 'alz' ? 'bg-[#8fb9de]/10 text-[#8fb9de]'
                                        : 'text-white/55 hover:bg-white/[0.03]'}`}
          >
            <HandCoins size={15} /> ALZ
          </button>
        </div>

        {loading ? (
          <div className="py-20 flex items-center justify-center text-white/40">
            <Loader2 size={22} className="animate-spin" />
          </div>
        ) : tab === 'itens' ? (
          /* ───────── ITENS ───────── */
          <div className="p-5">
            {items.length === 0 ? (
              <div className="py-14 text-center text-white/40 text-sm">
                Nenhum item à venda no momento.
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {items.map(it => (
                  <div key={it.listingId}
                       className="bg-[#0d141b] border border-white/[0.06] rounded-xl p-3 flex flex-col">
                    <div className="flex items-start gap-3">
                      <ItemIcon itemId={it.baseItemId} />
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-bold text-white leading-tight">
                          {it.itemName || `Item #${it.baseItemId ?? '?'}`}
                        </p>
                        <p className="text-[10px] text-white/45 mt-0.5">{it.sellerName}</p>
                      </div>
                    </div>
                    <div className="flex flex-wrap gap-1 mt-2">
                      {itemBadges(it).map((b, i) => (
                        <span key={i} className={`text-[9px] px-1.5 py-0.5 rounded font-bold ${b.cls}`}>
                          {b.text}
                        </span>
                      ))}
                    </div>
                    <div className="mt-auto pt-3 flex items-center justify-between gap-2">
                      <span className="text-base font-black text-[#8fb9de]">
                        {fmtBrl(it.priceBrl)}
                      </span>
                      <button
                        onClick={() => handleBuyItem(it)}
                        disabled={buyingItem !== null || chars.length === 0}
                        className="px-3 py-1.5 rounded-lg text-xs font-black text-[#0d141b]
                                   bg-gradient-to-r from-amber-400 to-amber-500
                                   hover:from-amber-300 hover:to-amber-400 transition cursor-pointer
                                   disabled:opacity-50 flex items-center gap-1.5"
                      >
                        {buyingItem === it.listingId
                          ? <Loader2 size={12} className="animate-spin" />
                          : <ShoppingCart size={12} />}
                        Comprar
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        ) : (
          /* ───────── ALZ ───────── */
          <div className="p-5 max-w-md">
            <div className="mb-4">
              <label className="text-xs font-bold text-white/70 flex items-center gap-1.5 mb-1.5">
                <Coins size={13} className="text-[#8fb9de]" /> Vendedor
              </label>
              {vendors.length === 0 ? (
                <p className="text-[11px] text-amber-400 py-2">
                  Nenhum vendedor de alz disponível no momento.
                </p>
              ) : (
                <select
                  value={vendorUserNum ?? ''}
                  onChange={(e) => setVendorUserNum(Number(e.target.value))}
                  className="w-full bg-[#0d141b] border border-white/[0.06] rounded-lg
                             px-4 py-2.5 text-sm text-white"
                >
                  {vendors.map(v => (
                    <option key={v.userNum} value={v.userNum}>
                      {v.sellerName} — {fmtBrl(v.alzPriceBrl)}/100kk · até {fmtAlzShort(v.availableAlz)}
                    </option>
                  ))}
                </select>
              )}
            </div>

            <div className="mb-4">
              <label className="text-xs font-bold text-white/70 block mb-1.5">
                Número de unidades (N)
              </label>
              <input
                type="number" min={1} max={maxUnits} value={units}
                onChange={(e) => setUnits(Math.min(maxUnits, Math.max(1, Number(e.target.value) || 1)))}
                className="w-full bg-[#0d141b] border border-white/[0.06] rounded-lg
                           px-4 py-2.5 text-sm text-white"
              />
              <p className="text-[11px] text-white/45 mt-1">
                Cada unidade = <span className="text-[#8fb9de] font-bold">{fmtAlzShort(unitAlz)} ALZ</span>
                {selectedVendor && <span className="text-white/35"> · máx {maxUnits} un.</span>}
              </p>
            </div>

            <div className="bg-gradient-to-br from-[#0d141b] to-[#11202a] border border-white/[0.06]
                            rounded-xl p-4 text-center mb-4">
              <p className="text-[10px] font-bold text-white/45 uppercase tracking-wider">
                Total
              </p>
              <p className="text-2xl font-black text-white mt-1">
                {fmtAlzShort(totalAlz)} <span className="text-sm text-white/40">ALZ</span>
              </p>
              <p className="text-sm font-bold text-[#8fb9de] mt-0.5">{fmtBrl(totalBrl)}</p>
            </div>

            <button
              onClick={handleBuyAlz}
              disabled={buyingAlz || vendors.length === 0 || chars.length === 0}
              className="w-full py-3 rounded-lg font-black text-sm text-[#0d141b]
                         bg-gradient-to-r from-amber-400 to-amber-500
                         hover:from-amber-300 hover:to-amber-400 transition cursor-pointer
                         disabled:opacity-50 flex items-center justify-center gap-2"
            >
              {buyingAlz ? <Loader2 size={15} className="animate-spin" /> : <ShoppingCart size={15} />}
              Comprar ALZ
            </button>
          </div>
        )}
      </div>

      <p className="text-[11px] text-white/35 mt-4 text-center">
        Modo sandbox — compras gratuitas para demonstração. A entrega é feita por correio in-game.
      </p>
    </div>
  );
}
